if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    class StickyAddToCart extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        this.productId = this.dataset.productId;

        this.imageElement = this.querySelector('[data-sticky-atc-image]');
        this.priceElement = this.querySelector('[data-sticky-atc-price]');
        this.variantSelect = this.querySelector('[data-sticky-atc-variant-select]');
        this.quantityInput = this.querySelector('[data-sticky-atc-quantity]');
        this.submitButton = this.querySelector('[data-sticky-atc-submit]');
        this.submitButtonText = this.querySelector('[data-sticky-atc-submit-text]');

        this.observer = null;
        this.variantUnsubscriber = null;
      }

      connectedCallback() {
        this.initObserver();
        this.initEventListeners();
        this.initVariantSync();
        this.initQuantitySync();
        this.initButtonStateObserver();
      }

      disconnectedCallback() {
        if (this.observer) {
          this.observer.disconnect();
        }
        if (typeof this.variantUnsubscriber === 'function') {
          this.variantUnsubscriber();
        }
      }

      get mainSubmitButton() {
        return document.getElementById(`ProductSubmitButton-${this.sectionId}`) ||
               document.querySelector(`#product-form-${this.sectionId} [type="submit"]`);
      }

      get mainProductForm() {
        return document.getElementById(`product-form-${this.sectionId}`);
      }

      get mainQuantityInput() {
        return document.getElementById(`Quantity-${this.sectionId}`);
      }

      get mainVariantPicker() {
        return document.getElementById(`variant-selects-${this.sectionId}`) ||
               document.querySelector(`variant-selects[data-section="${this.sectionId}"]`) ||
               document.querySelector(`variant-radios[data-section="${this.sectionId}"]`);
      }

      /* 1. Intersection Observer for trigger visibility */
      initObserver() {
        const target = this.mainSubmitButton || this.mainProductForm;
        if (!target) return;

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Main button is out of viewport (scrolled past down)
              const isPastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;

              if (isPastButton) {
                this.show();
              } else {
                this.hide();
              }
            });
          },
          {
            threshold: 0,
            rootMargin: '0px 0px 0px 0px',
          }
        );

        this.observer.observe(target);
      }

      show() {
        this.classList.add('sticky-atc--active', 'is-active');
        document.body.classList.add('has-sticky-atc');
      }

      hide() {
        this.classList.remove('sticky-atc--active', 'is-active');
        document.body.classList.remove('has-sticky-atc');
      }

      /* 2. Event Listeners */
      initEventListeners() {
        if (this.submitButton) {
          this.submitButton.addEventListener('click', (e) => {
            e.preventDefault();
            const mainBtn = this.mainSubmitButton;
            if (mainBtn && !mainBtn.hasAttribute('disabled')) {
              mainBtn.click();
            }
          });
        }
      }

      /* 3. Variant Selection Synchronization */
      initVariantSync() {
        // Sticky dropdown change -> update main form
        if (this.variantSelect) {
          this.variantSelect.addEventListener('change', (e) => {
            const selectedVariantId = e.target.value;
            const selectedOption = e.target.options[e.target.selectedIndex];

            // Update main form variant input
            const mainForm = this.mainProductForm;
            if (mainForm) {
              const mainIdInput = mainForm.querySelector('input[name="id"]');
              if (mainIdInput) {
                mainIdInput.value = selectedVariantId;
                mainIdInput.dispatchEvent(new Event('change', { bubbles: true }));
              }
            }

            // Sync with main variant picker controls (radios or selects)
            const mainPicker = this.mainVariantPicker;
            if (mainPicker) {
              const targetOptionInput = mainPicker.querySelector(`input[value="${selectedVariantId}"]`) ||
                                         mainPicker.querySelector(`option[value="${selectedVariantId}"]`);
              if (targetOptionInput) {
                if (targetOptionInput.tagName === 'OPTION') {
                  const selectEl = targetOptionInput.closest('select');
                  if (selectEl) {
                    selectEl.value = selectedVariantId;
                    selectEl.dispatchEvent(new Event('change', { bubbles: true }));
                  }
                } else if (targetOptionInput.type === 'radio') {
                  targetOptionInput.checked = true;
                  targetOptionInput.dispatchEvent(new Event('change', { bubbles: true }));
                }
              }
            }

            // Update sticky element display immediately
            this.updateStickyDisplayFromOption(selectedOption);
          });
        }

        // Listen for Theme PubSub variant changes
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event.data.sectionId === this.sectionId && event.data.variant) {
              this.updateStickyFromVariant(event.data.variant, event.data.html);
            }
          });
        }

        // Fallback: Listen to change events on main form / main variant picker
        document.addEventListener('change', (e) => {
          if (this.mainVariantPicker && this.mainVariantPicker.contains(e.target)) {
            setTimeout(() => this.syncFromMainVariantPicker(), 50);
          }
        });
      }

      updateStickyDisplayFromOption(option) {
        if (!option) return;

        const isAvailable = option.dataset.available === 'true';
        const imageUrl = option.dataset.image;

        // Image
        if (this.imageElement && imageUrl) {
          this.imageElement.src = imageUrl;
        }

        // Button availability
        if (this.submitButton) {
          if (isAvailable) {
            this.submitButton.removeAttribute('disabled');
            if (this.submitButtonText) {
              this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
            }
          } else {
            this.submitButton.setAttribute('disabled', 'disabled');
            if (this.submitButtonText) {
              this.submitButtonText.textContent = window.variantStrings?.soldOut || 'Sold out';
            }
          }
        }
      }

      updateStickyFromVariant(variant, html) {
        if (!variant) {
          if (this.submitButton) {
            this.submitButton.setAttribute('disabled', 'disabled');
            if (this.submitButtonText) {
              this.submitButtonText.textContent = window.variantStrings?.unavailable || 'Unavailable';
            }
          }
          return;
        }

        // Update variant dropdown selection
        if (this.variantSelect) {
          this.variantSelect.value = variant.id;
        }

        // Update image
        if (this.imageElement && variant.featured_media) {
          this.imageElement.src = variant.featured_media.preview_image?.src || variant.featured_image?.src || this.imageElement.src;
        }

        // Update price from updated HTML or variant object
        if (this.priceElement && html) {
          const newPrice = html.querySelector(`#price-${this.sectionId}`);
          if (newPrice) {
            this.priceElement.innerHTML = newPrice.innerHTML;
          }
        }

        // Update button state
        if (this.submitButton) {
          if (variant.available) {
            this.submitButton.removeAttribute('disabled');
            if (this.submitButtonText) {
              this.submitButtonText.textContent = window.variantStrings?.addToCart || 'Add to cart';
            }
          } else {
            this.submitButton.setAttribute('disabled', 'disabled');
            if (this.submitButtonText) {
              this.submitButtonText.textContent = window.variantStrings?.soldOut || 'Sold out';
            }
          }
        }
      }

      syncFromMainVariantPicker() {
        const mainForm = this.mainProductForm;
        if (!mainForm) return;
        const mainIdInput = mainForm.querySelector('input[name="id"]');
        if (mainIdInput && this.variantSelect) {
          this.variantSelect.value = mainIdInput.value;
          const selectedOpt = this.variantSelect.options[this.variantSelect.selectedIndex];
          if (selectedOpt) {
            this.updateStickyDisplayFromOption(selectedOpt);
          }
        }
      }

      /* 4. Quantity Synchronization */
      initQuantitySync() {
        if (!this.quantityInput) return;

        // Sticky quantity -> Main quantity
        this.quantityInput.addEventListener('change', () => {
          const mainQty = this.mainQuantityInput;
          if (mainQty) {
            mainQty.value = this.quantityInput.value;
            mainQty.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });

        // Main quantity -> Sticky quantity
        const mainQty = this.mainQuantityInput;
        if (mainQty) {
          mainQty.addEventListener('change', () => {
            this.quantityInput.value = mainQty.value;
          });
        }
      }

      /* 5. Mirror main submit button loading and disabled states */
      initButtonStateObserver() {
        const mainBtn = this.mainSubmitButton;
        if (!mainBtn) return;

        const observer = new MutationObserver(() => {
          if (!this.submitButton) return;

          if (mainBtn.classList.contains('loading')) {
            this.submitButton.classList.add('loading');
          } else {
            this.submitButton.classList.remove('loading');
          }

          if (mainBtn.hasAttribute('disabled')) {
            this.submitButton.setAttribute('disabled', 'disabled');
          } else {
            this.submitButton.removeAttribute('disabled');
          }
        });

        observer.observe(mainBtn, {
          attributes: true,
          attributeFilter: ['class', 'disabled'],
        });
      }
    }
  );
}
